import React, { useState, useRef, useEffect } from "react";
import content from "./content.json";
import "./App.css";
import Vim from "./Vim";
import { Link, useNavigate } from "react-router-dom";
const excludedProjects = [
  "waterfall",
  "equi",
  "spectral-arctic-buoys",
  "interactive-timeline",
]

function App() {
  const navigate = useNavigate();
  const contactCommand = () => {
    return (
      <div className="contact-list">
        Here are a few ways to get in touch with me or see what I'm up to:<br /><br />
        <ul>
          <li> Contact me via <a href="mailto:karun.ram.25@dartmouth.edu" target="_blank" rel="noreferrer">email</a> </li>
          <li> Connect with me on <a href="https://www.linkedin.com/in/karun-ram/" target="_blank" rel="noreferrer">LinkedIn</a> </li>
          <li> Check out my <a href="https://github.com/KamiV2" target="_blank" rel="noreferrer">GitHub</a> </li>
        </ul>
      </div>
    );
  };

  const helpCommand = () => {
    return (
      <div className="help">
        <ul>
          <li><b>help</b>: displays list of available commands</li>
          <li><b>welcome</b>: displays the welcome message</li>
          <li><b>about</b>: displays information about me</li>
          <li><b>work</b>: displays information about my work experience</li>
          <li><b>thesis</b>: displays information about my thesis</li>
          <li><b>projects</b>: displays information about my projects</li>
          <li><b>contact</b>: displays ways to get in touch with me</li>
          <li><b>simplify</b>: switches to a simpler version of this site</li>
          <li><b>clear</b>: clears the terminal screen</li>
        </ul>
      </div>
    )};

  const welcomeCommand = () => {
    return (
      <div className="welcome">
        Welcome! Type <b>'help'</b> for a list of commands, or click <Link to="/simple">here</Link> for a less interactive version of this site.
      </div>
    );
  };

  const handleNav = () => {
    navigate("/simple");
  };
  const getProjects = async () => {
    try {
        const response = await fetch("https://api.github.com/users/KamiV2/repos");
        
        if (!response.ok) {
            console.log(response);
            return ["Unable to fetch"];
        }

        const data = await response.json();
        const filteredData = data.filter(repo => !excludedProjects.includes(repo.name));
        const formattedRepos = filteredData.map(repo => {
            const name = repo.name || "No name";
            const url = repo.html_url || "#";
            const description = repo.description || "No description available";
            const language = repo.language || "Unknown language";
            return [`<a href="${url}">${name}</a>`, description, `Language: ${language}`, ""];
        });

        const flattened = Array.from(Object.values(formattedRepos.flat(1)));
        flattened.pop();
        return [`Projects are fetched **live** from my <a href="https://github.com/KamiV2/">GitHub</a> account.`, ""].concat(flattened);
    } catch (error) {
        console.error("Error fetching repositories:", error);
        return [];
    }
};
  const [history, setHistory] = useState([{ input: "welcome", output: welcomeCommand() }]);
  const [input, setInput] = useState("");
  const [isVimOpen, setIsVimOpen] = useState(false);
  const [vimContent, setVimContent] = useState([]);
  const [vimTitle, setVimTitle] = useState([]);
  const outputEndRef = useRef(null);
  const inputRef = useRef(null);
  const didYouMean = (command) => {
    const edit = (a, b) => {
      const m = a.length;
      const n = b.length;
      const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
      for (let i = 0; i <= m; i++) {
        dp[i][0] = i;
      }
      for (let j = 0; j <= n; j++) {
        dp[0][j] = j;
      }
      for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
          if (a[i - 1] === b[j - 1]) {
            dp[i][j] = dp[i - 1][j - 1];
          } else {
            dp[i][j] = Math.min(
              dp[i - 1][j] + 1,  // Deletion
              dp[i][j - 1] + 1,  // Insertion
              dp[i - 1][j - 1] + 1 // Substitution
            );
          }
        }
      }
      return dp[m][n];
    };
    const commands = ["help", "about", "work", "thesis", "projects", "welcome", "contact", "clear", "simplify"];
    for (let cmd in commands) {
      if (edit(commands[cmd], command) < 3) {
        return commands[cmd];
      }
    }
    return null;
  };

  // Command logic
  const formatWork = (workEntries) => {
    const lines = ["**Here's a brief overview of my work experience.**", ""];
    workEntries.forEach((w) => {
      lines.push(`${w.title} @ <a href=\"${w.companyLink}\">${w.company}</a>`);
      lines.push(`|  [${w.dates}]`);
      lines.push(`|  *${w.location}*`);
      lines.push(`|  ${w.description}`);
      lines.push("");
    });
    return lines;
  };

  const formatThesis = (t) => {
    const lines = [];
    lines.push(`${t.flavor}`);
    lines.push(`Title: *${t.title}* (<a href=\"${t.link}\">Link</a>)`);
    if (t.advisor) {
      if (t.advisorLink) lines.push(`Advisor: <a href=\"${t.advisorLink}\">${t.advisor}</a>`);
      else lines.push(`Advisor: ${t.advisor}`);
    }
    if (t.abstract) {
      for (let i = 0; i < t.abstract.length; i++) {
        if (i == 0) {
          lines.push(`Abstract: ${t.abstract[i]}`);
        } else {
          lines.push(t.abstract[i]);
        }
      }
    }
    return lines;
  };

  const commands = {
    help: helpCommand(),
    about: () => {
      setVimContent(content.about);
      setVimTitle("Home > about.txt");
      setIsVimOpen(true);
    },
    work: () => {
      setVimContent(formatWork(content.work));
      setVimTitle("Home > work.txt");
      setIsVimOpen(true);
    },
    projects: async () => {
      setVimContent(await getProjects());
      setVimTitle("Home > projects.txt");
      setIsVimOpen(true);
    },
    thesis: () => {
      setVimContent(formatThesis(content.thesis));
      setVimTitle("Home > thesis.txt");
      setIsVimOpen(true);
    },
    simplify: handleNav,
    welcome: welcomeCommand(),
    contact: contactCommand(),
    clear: "clear"
  };

  const handleCommand = (command) => {
    if (command === "clear") {
      setHistory([]);
      return;
    }
    if (commands[command]) {
      if (command === "about" || command === "work" || command === "thesis" || command === "simplify" || command === "projects") {
        setHistory([...history, { input: command, output: "" }]);
        commands[command]();
      } else {
        setHistory([...history, { input: command, output: commands[command] }]);
      }
    } else {
      const suggestion = didYouMean(command);
      if (suggestion) {
        setHistory([
          ...history,
          { input: command, output: `Command not found. Did you mean '${suggestion}'?` },
        ]);
      } else {
        setHistory([
          ...history,
          { input: command, output: "Command not found. Type 'help' for a list of commands." },
        ]);
      }
    }
  };

  const handleInput = (e) => {
    e.preventDefault();
    if (input.trim() !== "") {
      handleCommand(input.trim());
      setInput("");
    }
  };

  const handleVimClose = () => {
    setIsVimOpen(false);
    setTimeout(() => {
      outputEndRef.current?.scrollIntoView({ behavior: "auto" });
    }, 0);
  };

  useEffect(() => {
    outputEndRef.current?.scrollIntoView({ behavior: "auto" });
  }, [history]);

  // Handle up arrow key press to populate input with last history input
  const handleKeyDown = (e) => {
    if (e.key === "ArrowUp") {
      const lastHistoryInput = history[history.length - 1]?.input || "";
      setInput(lastHistoryInput);
    }
  };

  // Move cursor to the end of the input field when the input changes
  useEffect(() => {
    if (inputRef.current) {
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length); // Set cursor at the end
    }
  }, [input]); // This will run whenever `input` changes


  return (
    <div className="App">
      {isVimOpen ? (
        <div className="terminal">
          <Vim content={vimContent} title={vimTitle} onClose={handleVimClose} />
        </div>
      ) : (
        <div className="terminal">
          <div className="terminal-output">
            {history.map((entry, index) => (
              <div key={index}>
                <div className="terminal-input"><b><span id="username">root</span>@kamiv2$</b> {entry.input}</div>
                <div className="terminal-response">{entry.output}</div>
              </div>
            ))}
            <div ref={outputEndRef}></div>
          </div>
          <form className="terminal-form" onSubmit={handleInput}>
            <span className="prompt"><b><span id="username">root</span>@kamiv2$ </b></span>
            <input
              ref={inputRef} // Attach the ref to the input element
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown} // Add keydown event listener
              autoFocus
              className="terminal-input-box"
            />
          </form>
        </div>
      )}
    </div>
  );
}
export default App;